import React, { useEffect, useState } from "react";
import axios from '../../services/axios';
import { useStoreActions, useStoreState } from "easy-peasy";
import HelperMetiers360 from "../../services/HelpersMetiers360";
import ChartComponent from "../webappStats/ChartComponent";
import { Button, Col, Row } from "react-bootstrap";
import Loader from "../widgets/Loader";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from 'date-fns/locale/fr';
import SelectMultipleItemsComponent from "../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent";
import './WebappGlobalStatComponent.scss';
import { useModal } from "../../hooks/useModal";
import WebappAdminGlobalStatTableComponent from "./WebappAdminGlobalStatTableComponent";

registerLocale('fr', fr);

const WebappAdminStatComponent = () => {
    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);
    const { clientsCategories } = useStoreState(state => state.clients);
    const { fetchClientsCategories } = useStoreActions(actions => actions.clients);
    const { modalComponent, setModalShow, modalData, setModalData } = useModal();
    const [isFetching, setIsFetching] = useState(false);
    const [graphsData, setGraphsData] = useState({});
    const [isAvailableData, setIsAvailableData] = useState({});
    const [globalStatsData, setGlobalStatsData] = useState([]);
    const [isGlobalStatsFetching, setIsGlobalStatsFetching] = useState(false);
    const [minDate, setMinDate] = useState();
    const [maxDate, setMaxDate] = useState();
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [datesFilter, setDatesFilter] = useState([undefined, undefined]);
    const [categoriesFilter, setCategoriesFilter] = useState([]);

    const fetchDataForGraph = async () => {
        if (!ACTIONS?.webappStats?.admin || isFetching) return;

        setIsFetching(true);
        const entries = Object.entries(ACTIONS.webappStats?.admin);

        try {
            const results = await Promise.allSettled(
                entries
                    ?.filter(([key]) => key !== "globalStatsByClient")
                    ?.map(async ([key, stat]) => {
                        if (!stat.url) return [key, null];
                        const response = await axios.post(stat.url, {
                            categories: categoriesFilter,
                            startDate: datesFilter[0],
                            endDate: datesFilter[1]
                        }, { responseType: "json" });
                        return [key, response];
                    })
            );

            const dataObject = Object.fromEntries(results.filter(r => r.status === "fulfilled").map(r => r.value));
            const graphDataObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, value?.length ? value : []]));
            const isAvailableObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, !!value?.length]));

            setGraphsData(graphDataObject);
            setIsAvailableData(isAvailableObject);
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsFetching(false);
        }
    };

    const fetchGlobalStats = async () => {
        if (!ACTIONS?.webappStats?.admin?.globalStatsByClient?.url || isGlobalStatsFetching) return;

        setIsGlobalStatsFetching(true);

        try {
            const res = await axios.get(
                ACTIONS?.webappStats?.admin?.globalStatsByClient?.url,
                { responseType: "json" }
            );

            setGlobalStatsData(res);
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsGlobalStatsFetching(false);
        }
    }

    useEffect(() => {
        fetchDataForGraph();
        fetchGlobalStats();
        if (clientsCategories.length == 0) {
            fetchClientsCategories();
        };
    }, []);

    useEffect(() => {
        fetchDataForGraph();
    }, [datesFilter, categoriesFilter]);

    const openGlobalStats = () => {
        setModalData({
            ...modalData,
            header: <>Statistiques globales par client</>,
            content: <WebappAdminGlobalStatTableComponent
                tableCsv={tableCsv.globalStatsByClient}
                clientsCategories={clientsCategories}
            />,
            cancelButton: 'OK',
            size: 'xl'
        });
        setModalShow(true);
    }

    const dateFilter = <div>
        <label className="label-select">Période&nbsp;:</label>
        <div className="d-flex align-items-center">
            <Button className="shadow btnInitFilters ms-0" variant="danger" size="sm" onClick={() => setMinDate(null)}>
                <i className="fas fa-redo"></i>
            </Button>
            <DatePicker
                showIcon
                locale='fr'
                id='start'
                dateFormat="dd/MM/yyyy"
                selected={minDate}
                onChange={(date) => setMinDate(date)}
                placeholderText="jj/mm/aaaa"
                maxDate={maxDate}
            />
            <label htmlFor="end" className="m-2">&nbsp;au</label>
            <Button className="shadow btnInitFilters" variant="danger" size="sm" onClick={() => setMaxDate(null)}>
                <i className="fas fa-redo"></i>
            </Button>
            <DatePicker
                showIcon
                locale='fr'
                id='end'
                dateFormat="dd/MM/yyyy"
                selected={maxDate}
                onChange={(date) => setMaxDate(date)}
                placeholderText="jj/mm/aaaa"
                minDate={minDate}
            />
        </div>
    </div>;

    const categoryFilter = clientsCategories?.length > 0 && <div>
        <label className="label-select">Catégories&nbsp;d'établissement&nbsp;: </label>
        <SelectMultipleItemsComponent itemsSelected={selectedCategories} setItemsSelected={setSelectedCategories}
            items={clientsCategories} name="contacts" itemKey="id" itemValue="name" id='select-category' />
    </div>;

    const userAccountsLegendNewlabels = {
        "user_with_activities_done": "Utilisateur⋅ices avec au - 1 activité terminée",
        "user_without_activity_done_but_with_current_activities": "Utilisateur⋅ices sans activité terminée mais avec au - 1 activité en cours",
        "user_without_activity": "Utilisateur⋅ices sans activité terminée ou en cours"
    };

    const tableCsv = {
        activeUsersByClientCategory: graphsData?.activeUsersByClientCategory?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count, b.count)),
        userAccounts: graphsData?.userAccounts?.map(data => {
            return {
                users: userAccountsLegendNewlabels[data.users],
                count: data.count
            }
        })?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count, b.count)),
        globalStatsByClient: globalStatsData?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.user_count, b.user_count))
    };

    const csvSort = {
        activeUsersByClientCategory: [
            {
                value: 'category',
                label: 'Catégorie client',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activeUsersByClientCategory, "categories"),
                display: (data) => data.categories
            },
            {
                value: 'count',
                label: 'Nombre de comptes utilisateur⋅ices actifs',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activeUsersByClientCategory, "count"),
                display: (data) => data.count
            }
        ],
        userAccounts: [
            {
                value: 'users',
                label: 'Utilisateur⋅ices',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.userAccounts, "users"),
                display: (data) => data.users
            },
            {
                value: 'count',
                label: 'Nombre',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.userAccounts, "count"),
                display: (data) => data.count
            }
        ]
    };

    return <>
        {modalComponent}
        {isGlobalStatsFetching
            ? <Loader noMargin={true} />
            : Object.keys(graphsData).length !== 0 && <Row className="justify-content-center">
                <Button variant="primary" onClick={openGlobalStats} className="w-auto">
                    Statistiques globales par établissement
                </Button>
            </Row>
        }
        <Row className="d-flex justify-content-between align-items-end mb-4">
            <Col>{dateFilter}</Col>
            <Col lg="4">{categoryFilter}</Col>
            <Col lg="2" className="d-flex justify-content-end">
                <Button id="btnInitFilters" className="shadow" variant="success" size="sm"
                    onClick={() => {
                        setDatesFilter([minDate, maxDate]);
                        setCategoriesFilter(selectedCategories?.map(cat => cat.name))
                    }}>
                    Filtrer
                </Button>
            </Col>
        </Row>
        {isFetching
            ? <Loader />
            : Object.keys(graphsData).length !== 0 && <>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Comptes utilisateur⋅ices par catégorie d'établissements (avec au - une activité terminée)"
                            tableCsv={tableCsv?.activeUsersByClientCategory}
                            csvSort={csvSort?.activeUsersByClientCategory}
                            csvFilename="webapp_comptes_utilisateurices_actifs_par_catégorie_établissement"
                            graphType="pie"
                            data={graphsData?.activeUsersByClientCategory}
                            isDataAvailable={isAvailableData?.activeUsersByClientCategory}
                            dataNamesLabel="categories"
                            dataValuesLabel="count"
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title="Comptes utilisateur⋅ices"
                            tableCsv={tableCsv?.userAccounts}
                            csvSort={csvSort?.userAccounts}
                            csvFilename="webapp_comptes_utilisateurices"
                            graphType="pie"
                            data={graphsData?.userAccounts}
                            isDataAvailable={isAvailableData?.userAccounts}
                            dataNamesLabel="users"
                            dataValuesLabel="count"
                            legendNewlabels={userAccountsLegendNewlabels}
                        />
                    </Col>
                </Row>
            </>
        }
    </>
}

export default WebappAdminStatComponent;